/* DoubleOak — 21+ age verification gate.
   Industry-standard alcohol-site pattern: modal overlay shown on first
   visit, blocks the page until confirmed, persists for 30 days via
   localStorage, redirects under-21 users to responsibility.org. */

import React, { useState, useEffect, useRef } from 'react';

const STORAGE_KEY = 'do-age-verified';
const TTL_DAYS = 30;
const DENY_URL = 'https://www.responsibility.org/';

function readStored() {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return Boolean(data && typeof data.expires === 'number' && data.expires > Date.now());
  } catch {
    return false;
  }
}

export function LpAgeGate() {
  // Resolve the initial state from localStorage so the gate either
  // appears immediately on first paint or doesn't appear at all —
  // no flash of the gate for already-verified visitors.
  const [verified, setVerified] = useState(() => {
    if (typeof window === 'undefined') return true;
    return readStored();
  });
  const yesBtnRef = useRef(null);
  const noBtnRef = useRef(null);

  useEffect(() => {
    if (verified) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    yesBtnRef.current?.focus();

    // Hide background landmarks from AT and remove them from the tab order
    // while the dialog is open. `inert` covers keyboard + AT in modern
    // browsers; aria-hidden is a belt-and-suspenders for older AT.
    const main = document.getElementById('main');
    if (main) {
      main.setAttribute('inert', '');
      main.setAttribute('aria-hidden', 'true');
    }

    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); return; }
      if (e.key !== 'Tab') return;
      // Focus trap — cycle between Yes and No buttons.
      const yes = yesBtnRef.current;
      const no = noBtnRef.current;
      if (!yes || !no) return;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === yes) { e.preventDefault(); no.focus(); }
      } else {
        if (active === no) { e.preventDefault(); yes.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (main) {
        main.removeAttribute('inert');
        main.removeAttribute('aria-hidden');
      }
    };
  }, [verified]);

  const confirm = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        verified: true,
        ts: Date.now(),
        expires: Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000,
      }));
    } catch {}
    setVerified(true);
  };

  const deny = () => { window.location.href = DENY_URL; };

  if (verified) return null;

  return (
    <div
      className="lp-age-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      aria-describedby="age-gate-desc"
    >
      <div className="lp-age-gate__backdrop" aria-hidden="true"></div>
      <div className="lp-age-gate__panel">
        <div className="lp-age-gate__brand">
          <img src="/logo.svg" alt="" />
          <span className="word">Double<em>Oak</em></span>
        </div>
        <h2 id="age-gate-title" className="lp-age-gate__title">
          Are you <em>21</em> or older?
        </h2>
        <p id="age-gate-desc" className="lp-age-gate__lede">
          You must be 21 to enter this site.
        </p>
        <div className="lp-age-gate__actions">
          <button
            type="button"
            className="lp-cta-primary lp-age-gate__yes"
            onClick={confirm}
            ref={yesBtnRef}
          >
            Yes, I'm 21 or older
            <span aria-hidden="true">→</span>
          </button>
          <button type="button" className="lp-age-gate__no" onClick={deny} ref={noBtnRef}>
            No, I'm under 21
          </button>
        </div>
        <p className="lp-age-gate__legal">
          By entering, you confirm you are 21 years of age or older.
          {' '}<strong>Please drink responsibly.</strong>
        </p>
      </div>
    </div>
  );
}
